import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
    Upload,
    Mail,
    Trash2,
    FileSpreadsheet,
    CheckCircle2,
    Clock,
    Briefcase,
    Download,
    AlertTriangle,
} from "lucide-react";

const EXCEL_TEMPLATE_ROWS = [
    ["Full Name", "Email", "Phone"],
    ["Jane Chan", "jane.chan@example.com", "+852 5555 0001"],
    ["Sam Wong", "sam.wong@example.com", "+852 5555 0002"],
];

function toCsv(rows) {
    return rows
        .map((r) =>
            r
                .map((c) => {
                    const s = String(c ?? "");
                    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                })
                .join(",")
        )
        .join("\n");
}

export default function Employees() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [lastReport, setLastReport] = useState(null);
    const fileInputRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/member/employees");
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user?.membership_type === "business") load();
    }, [user?.membership_type, load]);

    const upload = async (file) => {
        setUploading(true);
        setLastReport(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/member/employees/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLastReport(data);
            toast.success(
                `${data.created} employee${data.created === 1 ? "" : "s"} invited from ${data.filename}`
            );
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const downloadTemplate = () => {
        const blob = new Blob([toCsv(EXCEL_TEMPLATE_ROWS)], {
            type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "perx-employees-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const resend = async (emp) => {
        try {
            await api.post(`/member/employees/${emp.id}/resend-invite`);
            toast.success(`Invite resent to ${emp.email}`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (emp) => {
        if (
            !window.confirm(
                `Remove ${emp.name} (${emp.email})? Their Perx account will be deleted.`
            )
        )
            return;
        try {
            await api.delete(`/member/employees/${emp.id}`);
            toast.success("Employee removed");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // Not a business admin? Gentle upsell instead of a raw 403.
    if (user?.membership_type !== "business") {
        return (
            <>
                <PageHeader
                    eyebrow="Corporate"
                    title="My employees"
                    description="Enrol your whole team on Perx with an Excel upload."
                />
                <div className="px-6 lg:px-12 py-8">
                    <div
                        data-testid="employees-not-business"
                        className="rounded-2xl bg-white border border-navy-100 shadow-soft p-8 max-w-xl"
                    >
                        <Briefcase className="text-gold-600" size={22} />
                        <div className="font-display text-2xl text-navy-700 mt-2">
                            This tool is for Business memberships.
                        </div>
                        <p className="text-navy-500 text-sm mt-2">
                            Your account is currently on the{" "}
                            <span className="font-semibold text-navy-700">
                                {user?.membership_type || "individual"}
                            </span>{" "}
                            plan. Reach out to us to switch to a Business plan and unlock
                            bulk-enrolment for your team.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader
                eyebrow="Corporate"
                title={user?.company_name || "My employees"}
                description="Upload an Excel or CSV to invite your team. Each row becomes a Perx account with an activation email."
                action={
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={downloadTemplate}
                            data-testid="employees-download-template"
                            className="rounded-full border-navy-700 text-navy-700 h-11"
                        >
                            <Download size={16} className="mr-2" /> Template
                        </Button>
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="employees-upload-btn"
                            className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 h-11 px-5 font-semibold"
                        >
                            <Upload size={16} className="mr-2" />
                            {uploading ? "Uploading…" : "Upload Excel"}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.csv"
                            data-testid="employees-file-input"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) upload(f);
                            }}
                            className="hidden"
                        />
                    </div>
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="grid sm:grid-cols-3 gap-5">
                    <StatCard
                        label="Total employees"
                        value={rows.length}
                        testid="employees-stat-total"
                    />
                    <StatCard
                        label="Activated"
                        value={rows.filter((r) => r.status === "active").length}
                        testid="employees-stat-active"
                    />
                    <StatCard
                        label="Pending invite"
                        value={rows.filter((r) => r.status === "invited").length}
                        accent
                        testid="employees-stat-invited"
                    />
                </div>

                {lastReport && (
                    <UploadReport report={lastReport} />
                )}

                <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                    <Table data-testid="employees-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Name
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Email
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Phone
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Status
                                </TableHead>
                                <TableHead className="text-right" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center text-navy-500 py-10">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center text-navy-500 py-16">
                                        <div className="flex flex-col items-center gap-2">
                                            <FileSpreadsheet className="text-gold-500" size={28} />
                                            <div className="font-display text-lg text-navy-700">
                                                No employees yet
                                            </div>
                                            <div className="text-sm text-navy-500 max-w-md">
                                                Download the template, add your team, and upload
                                                the file. Everyone will get an activation email.
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid={`employee-row-${r.id}`}>
                                    <TableCell className="text-navy-700 font-medium">
                                        {r.name}
                                    </TableCell>
                                    <TableCell className="text-navy-500">{r.email}</TableCell>
                                    <TableCell className="text-navy-500">
                                        {r.phone || "—"}
                                    </TableCell>
                                    <TableCell>
                                        {r.status === "active" ? (
                                            <Badge className="bg-emerald-100 text-emerald-800 border-0 inline-flex items-center gap-1">
                                                <CheckCircle2 size={12} /> Active
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-gold-500/20 text-navy-700 border-0 inline-flex items-center gap-1">
                                                <Clock size={12} /> Pending
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            {r.status === "invited" && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    data-testid={`employee-resend-${r.id}`}
                                                    onClick={() => resend(r)}
                                                    className="text-navy-700 hover:text-navy-900"
                                                    title="Resend invite"
                                                >
                                                    <Mail size={14} />
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`employee-remove-${r.id}`}
                                                onClick={() => remove(r)}
                                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                                title="Remove"
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}

function StatCard({ label, value, accent, testid }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl shadow-soft p-6 ${
                accent ? "bg-navy-700 text-white" : "bg-white"
            }`}
        >
            <div
                className={`text-xs uppercase tracking-[0.3em] mb-2 ${
                    accent ? "text-gold-500" : "text-gold-600"
                }`}
            >
                {label}
            </div>
            <div className={`font-display text-4xl ${accent ? "" : "text-navy-700"}`}>
                {value}
            </div>
        </div>
    );
}

function UploadReport({ report }) {
    return (
        <div
            data-testid="employees-upload-report"
            className="rounded-2xl bg-white border border-gold-200/60 shadow-soft p-5"
        >
            <div className="flex items-center gap-2 mb-3">
                <FileSpreadsheet className="text-gold-600" size={18} />
                <div className="font-display text-lg text-navy-700">
                    {report.filename}
                </div>
                <Badge className="bg-navy-50 text-navy-700 border-navy-100 border ml-2">
                    {report.total_rows} rows parsed
                </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="rounded-lg bg-emerald-50 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-800">
                        Invited
                    </div>
                    <div className="font-display text-2xl text-emerald-900">
                        {report.created}
                    </div>
                </div>
                <div className="rounded-lg bg-red-50 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-red-700">
                        Skipped
                    </div>
                    <div className="font-display text-2xl text-red-800">
                        {report.skipped}
                    </div>
                </div>
            </div>
            {report.errors?.length > 0 && (
                <div className="rounded-lg bg-red-50/60 border border-red-100 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-red-700 mb-2 font-semibold">
                        <AlertTriangle size={12} /> Skipped rows
                    </div>
                    <ul className="text-xs text-red-800 space-y-1 max-h-40 overflow-y-auto">
                        {report.errors.map((e, i) => (
                            <li key={`${e.row}-${e.email || i}`}>
                                Row {e.row}
                                {e.email && ` (${e.email})`}: {e.reason}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
